import React, { useState, useEffect, useRef } from "react";

const ChatBot = () => {
  const [input, setInput] = useState("");
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const [userData, setUserData] = useState({ name: "", email: "", submitted: false });

  const apiUrl = "https://juancarlosmancharamos.ar/gemini_proxy.php";

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [responses, loading]);
  
  const handleUserSubmit = (e) => {
    e.preventDefault();
    if (userData.name && userData.email) {
      setUserData({ ...userData, submitted: true });
      saveToLog("system", `Usuario conectado: ${userData.name} (${userData.email})`);
    }
  };

  const saveToLog = async (role, text) => {
    try {
      await fetch("https://juancarlosmancharamos.ar/log_chat.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          role,
          text,
          userName: userData.name,
          userEmail: userData.email
        })
      });
    } catch (e) {
      console.error("Error log:", e);
    }
  };

  const handleSend = async () => {
    const trimmedInput = input.trim();
    if (!trimmedInput) return;

    setResponses((prev) => [...prev, { role: "user", text: trimmedInput }]);
    setInput("");
    saveToLog("user", trimmedInput);
    
    setLoading(true);

    // Prompt ajustado con la excepción para que no corte los datos de contacto
    const promptSystem = `Actúa como el Asistente Inteligente de Juan Carlos Mancha Ramos (JCMR). 
    Tu objetivo es ayudar a los visitantes de su web personal (juancarlosmancharamos.ar) a conocer su trabajo y servicios.

    PERFIL DE JUAN CARLOS:
    - Desarrollador Full Stack Senior con más de 10 años de experiencia.
    - Especialista en: React, Next.js, Node.js, Strapi, Docker y arquitectura de APIs.
    - Experto en E-commerce: WooCommerce y PrestaShop.
    - Cofundador de Disegno Web Bologna (Italia).

    REGLAS DE CONTACTO (CRÍTICO):
    1. Siempre que menciones el contacto, usa exactamente estos formatos de enlace Markdown:
    - WhatsApp: [ +54 351 2768803](https://wa.me/543512768803)
    - Email: [info@disegnowebologna.ar](mailto:info@disegnowebologna.ar)
    - Teléfono: [+54 351 2768803](tel:+543512768803)
    2. Asegúrate de que los enlaces sean claros para que el usuario pueda pulsar y comunicarse al instante.
    
    REGLA DE ORO DE BREVEDAD: Está prohibido responder con un único bloque de texto. Debes dividir siempre la respuesta en 2 párrafos distintos separados por un salto de línea. Cada párrafo debe tener máximo 2 oraciones. Termina el segundo párrafo con una pregunta directa.
    EXCEPCIÓN: Cuando el usuario pide datos de contacto, ignora esta regla de concisión y muestra el formato de lista exacto.
    
    CONSULTA DEL USUARIO: ${trimmedInput}`;

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ parts: [{ text: promptSystem }] }],
          generationConfig: { maxOutputTokens: 3000, temperature: 0.7 } // <-- Subimos los tokens a 800
        })
      });

      const data = await response.json();

      if (data?.candidates?.[0]?.content) {
        const botFullText = data.candidates[0].content.parts[0].text;
        
        // El tiempo de respuesta de la API sirve como delay para el primer párrafo
        setLoading(false);

        // Separamos inteligentemente por párrafos reales (doble enter), no por saltos simples
        const paragraphs = botFullText.split(/(?:\r?\n){2,}/).filter(p => p.trim() !== "");
        
        for (let i = 0; i < paragraphs.length; i++) {
          const paragraph = paragraphs[i];
          
          if (i > 0) {
            // Mostramos el "pensando" orgánico solo a partir de la segunda burbuja
            setLoading(true);
            const delayTime = Math.min(Math.max(paragraph.length * 15, 1200), 2500);
            await new Promise(resolve => setTimeout(resolve, delayTime));
            setLoading(false);
          }
          
          setResponses((prev) => [...prev, { role: "bot", text: paragraph }]);
        }

        saveToLog("bot", botFullText);
      }
    } catch (error) {
      console.error("Chat error:", error);
      setLoading(false);
    }
  };

  const formatMarkdownLinks = (text) => {
    let cleanedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    return cleanedText.replace(linkRegex, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: inherit; font-weight: bold; text-decoration: underline;">$1</a>')
      .replace(/\n/g, '<br />'); // Convierte saltos internos residuales en HTML visual
  };

  return (
    <div style={styles.container}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{ ...styles.fab, transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}
      >
        {isOpen ? "✕" : "💬"}
      </button>

      {isOpen && (
        <div style={styles.window}>
          {!userData.submitted ? (
            <form onSubmit={handleUserSubmit} style={styles.formContainer}>
              <h3 style={{ color: "#333", marginBottom: "10px" }}>¡Hola!</h3>
              <p style={{ fontSize: "14px", color: "#666", textAlign: "center" }}>Ingresá tus datos para comenzar.</p>
              <input
                required
                placeholder="Tu nombre"
                style={styles.inputForm}
                onChange={e => setUserData({ ...userData, name: e.target.value })}
              />
              <input
                required
                type="email"
                placeholder="Tu email"
                style={styles.inputForm}
                onChange={e => setUserData({ ...userData, email: e.target.value })}
              />
              <button type="submit" style={styles.sendBtnForm}>Empezar Chat</button>
            </form>
          ) : (
            <>
              <div style={styles.header}>
                <div style={styles.onlineStatus}></div>
                <span style={{ fontWeight: "600" }}>Asistente Virtual JCMR</span>
              </div>

              <div style={styles.chatArea}>
                {responses.length === 0 && (
                  <div style={styles.welcome}>¡Hola {userData.name}! ¿En qué puedo ayudarte hoy?</div>
                )}
                {responses.map((msg, i) => (
                  <div key={i} style={{
                    ...styles.messageWrapper,
                    display: "flex",
                    justifyContent: msg.role === "user" ? "flex-end" : "flex-start"
                  }}>
                    <div style={{
                      ...styles.bubble,
                      background: msg.role === "user" ? "#ffaf00" : "#ffffff",
                      color: msg.role === "user" ? "#fff" : "#333",
                      alignSelf: msg.role === "user" ? "flex-end" : "flex-start"
                    }}>
                      <span dangerouslySetInnerHTML={{ __html: formatMarkdownLinks(msg.text) }} />
                    </div>
                  </div>
                ))}
                {loading && <div style={styles.typing}>Juan Carlos está escribiendo...</div>}
                <div ref={messagesEndRef} />
              </div>

              <div style={styles.inputArea}>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSend(); }}
                  placeholder="Escribe un mensaje..."
                  style={styles.input}
                />
                <button onClick={handleSend} style={styles.sendBtn}>➤</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { position: "fixed", bottom: "20px", right: "20px", zIndex: 9999 },
  fab: { width: "60px", height: "60px", borderRadius: "30px", background: "#ffaf00", color: "white", fontSize: "24px", border: "none", boxShadow: "0 4px 15px rgba(255, 175, 0, 0.4)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  window: { position: "absolute", bottom: "70px", right: "0", width: window.innerWidth < 480 ? "calc(100vw - 40px)" : "350px", height: "500px", background: "#f8f9fa", borderRadius: "20px", display: "flex", flexDirection: "column", boxShadow: "0 10px 30px rgba(0,0,0,0.15)", overflow: "hidden" },
  header: { background: "#ffaf00", color: "white", padding: "15px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  chatArea: { flex: 1, overflowY: "auto", padding: "15px", display: "flex", flexDirection: "column", gap: "10px" },
  bubble: { padding: "10px 14px", borderRadius: "15px", fontSize: "14px", maxWidth: "85%", wordWrap: "break-word" },
  inputArea: { padding: "12px", background: "#fff", display: "flex", gap: "8px", borderTop: "1px solid #eee" },
  input: { flex: 1, border: "1px solid #ddd", borderRadius: "20px", padding: "8px 15px", outline: "none" },
  sendBtn: { background: "none", border: "none", color: "#ffaf00", fontSize: "20px", cursor: "pointer" },
  formContainer: { display: "flex", flexDirection: "column", padding: "30px", gap: "10px", justifyContent: "center", alignItems: "center", height: "100%", background: "#fff" },
  inputForm: { width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #ddd", outline: "none" },
  sendBtnForm: { width: "100%", padding: "12px", borderRadius: "8px", border: "none", background: "#ffaf00", color: "#fff", fontWeight: "bold", cursor: "pointer", marginTop: "10px" },
  onlineStatus: { width: "10px", height: "10px", borderRadius: "50%", background: "#4CAF50", marginRight: "8px" },
  typing: { fontSize: "12px", color: "#888", fontStyle: "italic", padding: "5px 15px" }
};

export default ChatBot;